"use client"

import { useMemo, useState } from "react"
import { FormikProvider, type FormikErrors, useFormik } from "formik"

import { FormInput } from "@/components/form-inputs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useCreateMetaAdsFormCopyMutation } from "@/hooks/mutations/meta-ads"
import type { MetaAdsFormCopyResult, MetaLeadForm } from "@/lib/meta-ads/types"
import { useTranslations } from "next-intl"


type FormCopyValues = {
  name: string
}

export function MetaAdsFormCopyDialog({
  sourceForm,
  onOpenChange,
  onCreated,
}: {
  sourceForm: MetaLeadForm | null
  onOpenChange: (open: boolean) => void
  onCreated: (result: MetaAdsFormCopyResult) => void
}) {
  const tUi = useTranslations("Integrations.IntegrationsMetaAdsMetaAdsFormCopyDialog")
  const tCommonUi = useTranslations("Common")

  const mutation = useCreateMetaAdsFormCopyMutation()
  const [error, setError] = useState<string | null>(null)
  const initialValues = useMemo(
    () => ({ name: getDefaultCopyName(sourceForm?.name ?? "Instant Form") }),
    [sourceForm?.name],
  )
  const formik = useFormik<FormCopyValues>({
    initialValues,
    enableReinitialize: true,
    validate: validateCopy,
    onSubmit: async values => {
      if (!sourceForm) return
      setError(null)
      try {
        const result = await mutation.mutateAsync({
          sourceFormId: sourceForm.id,
          name: values.name.trim(),
        })
        onCreated(result)
        handleOpenChange(false)
      } catch (submitError: unknown) {
        setError(getErrorMessage(submitError, tUi))
      }
    },
  })

  function handleOpenChange(open: boolean) {
    if (!open) {
      setError(null)
      formik.resetForm()
    }
    onOpenChange(open)
  }

  return (
    <Dialog open={Boolean(sourceForm)} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle><span key={tUi("createAnUnrestrictedCopy")}>{tUi("createAnUnrestrictedCopy")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("yourCurrentFormAndLiveAdWillStay")}>{tUi("yourCurrentFormAndLiveAdWillStay")}</span></DialogDescription>
        </DialogHeader>
        <FormikProvider value={formik}>
          <form className="space-y-4" onSubmit={formik.handleSubmit}>
            <p className="text-sm text-muted-foreground">
              <span key={tUi("versafyWillCreateASecondFormThatWorks")}>{tUi("versafyWillCreateASecondFormThatWorks")}</span></p>
            {error ? (
              <Alert variant="destructive" role="alert">
                <AlertTitle><span key={tUi("theFormCouldNotBeCopied")}>{tUi("theFormCouldNotBeCopied")}</span></AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <FormInput
              name="name"
              label={tUi("newInstantFormName")}
              description={tUi("useANameThatIsEasyToRecognize")}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button type="submit" disabled={mutation.isPending}>
                <span key={mutation.isPending ? tUi("creating") : tUi("createUnrestrictedCopy")}>{mutation.isPending ? tUi("creating") : tUi("createUnrestrictedCopy")}</span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function getDefaultCopyName(sourceName: string): string {
  return `${sourceName} — Versafy ready`.slice(0, 255)
}

function validateCopy(values: FormCopyValues): FormikErrors<FormCopyValues> {
  const name = values.name.trim()
  if (!name) return { name: "Enter a name for the copied form." }
  if (name.length > 255) return { name: "Use 255 characters or fewer." }
  return {}
}

function getErrorMessage(error: unknown, tUi: ReturnType<typeof useTranslations<"Integrations.IntegrationsMetaAdsMetaAdsFormCopyDialog">>): string {
  if (error instanceof Error && error.message) return error.message
  return tUi("theInstantFormCouldNotBeCopiedTry")
}
